import {
  Button,
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
  Field,
  FieldLabel,
  Input,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { XIcon } from 'lucide-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import type { ManualEntryCardProps } from './shared.types';

export const ManualEntryCard = ({
  entry,
  index,
  label,
  namePlaceholder,
  detailsPlaceholder,
  onUpdate,
  onRemove,
}: ManualEntryCardProps) => {
  const { t } = useTranslation('analysis');
  const nameId = useId();
  const detailsId = useId();
  const removeLabel = t('analysis.manualEntryCard.removeEntity', { label, index: index + 1 });

  return (
    <Card variant="outline" compact>
      <CardHeader compact>
        <CardTitle>
          {t('analysis.manualEntryCard.entityHeading', { label, index: index + 1 })}
        </CardTitle>
        {onRemove && (
          <CardAction>
            {/* Its own provider rather than the one in main.tsx, so the card stands alone
                in any host and in its own tests. */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={removeLabel}
                    onClick={() => onRemove(entry.id)}
                  >
                    <XIcon aria-hidden />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{removeLabel}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardAction>
        )}
      </CardHeader>
      <CardContent compact className="flex flex-col gap-sm">
        <Field>
          <FieldLabel htmlFor={nameId}>
            {t('analysis.manualEntryCard.entityName', { label })}
          </FieldLabel>
          <Input
            id={nameId}
            placeholder={namePlaceholder}
            value={entry.name}
            onChange={(event) => onUpdate(entry.id, { name: event.target.value })}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor={detailsId}>
            {t('analysis.manualEntryCard.entityDetails', { label })}
          </FieldLabel>
          <Textarea
            id={detailsId}
            placeholder={detailsPlaceholder}
            rows={3}
            value={entry.details}
            onChange={(event) => onUpdate(entry.id, { details: event.target.value })}
          />
        </Field>
      </CardContent>
    </Card>
  );
};
